import { useCallback, useEffect, useRef, useState } from 'react'

export const MAP_RAIL_HOVER_PREVIEW_DELAY_MS = 260
const PREVIEW_LEAVE_GRACE_MS = 80

type UseMapRailHoverPreviewOptions = {
  /** When true, hover previews are suppressed (e.g. pipeline popup open). */
  overlayBlocked: boolean
}

/**
 * Delayed hover preview for map overlay rails. Not sticky: preview hides when pointer
 * leaves the rail row and preview card (with a short grace period for rail → card moves).
 */
export function useMapRailHoverPreview({ overlayBlocked }: UseMapRailHoverPreviewOptions) {
  const [hoverId, setHoverId] = useState<string | null>(null)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const showDelayRef = useRef<number | null>(null)
  const hideDelayRef = useRef<number | null>(null)
  const previewCardHoverRef = useRef(false)

  const cancelShowDelay = useCallback(() => {
    if (showDelayRef.current != null) {
      window.clearTimeout(showDelayRef.current)
      showDelayRef.current = null
    }
  }, [])

  const cancelHideDelay = useCallback(() => {
    if (hideDelayRef.current != null) {
      window.clearTimeout(hideDelayRef.current)
      hideDelayRef.current = null
    }
  }, [])

  const clearPreview = useCallback(() => {
    cancelShowDelay()
    cancelHideDelay()
    previewCardHoverRef.current = false
    setHoverId(null)
    setPreviewId(null)
  }, [cancelHideDelay, cancelShowDelay])

  /** Rail click: show preview immediately and cancel hover timers. */
  const armFocusHover = useCallback(
    (id: string) => {
      if (overlayBlocked) return
      cancelShowDelay()
      cancelHideDelay()
      previewCardHoverRef.current = false
      setHoverId(id)
      setPreviewId(id)
    },
    [overlayBlocked, cancelHideDelay, cancelShowDelay],
  )

  useEffect(() => {
    if (overlayBlocked) clearPreview()
  }, [overlayBlocked, clearPreview])

  const scheduleHidePreview = useCallback(() => {
    cancelHideDelay()
    hideDelayRef.current = window.setTimeout(() => {
      hideDelayRef.current = null
      if (!previewCardHoverRef.current) {
        setPreviewId(null)
      }
    }, PREVIEW_LEAVE_GRACE_MS)
  }, [cancelHideDelay])

  const onHoverStart = useCallback(
    (id: string) => {
      if (overlayBlocked) return
      cancelHideDelay()
      setHoverId(id)
      cancelShowDelay()
      showDelayRef.current = window.setTimeout(() => {
        showDelayRef.current = null
        if (!overlayBlocked) setPreviewId(id)
      }, MAP_RAIL_HOVER_PREVIEW_DELAY_MS)
    },
    [overlayBlocked, cancelHideDelay, cancelShowDelay],
  )

  const onHoverEnd = useCallback(() => {
    setHoverId(null)
    cancelShowDelay()
    scheduleHidePreview()
  }, [cancelShowDelay, scheduleHidePreview])

  const onPreviewCardEnter = useCallback(() => {
    previewCardHoverRef.current = true
    cancelHideDelay()
  }, [cancelHideDelay])

  const onPreviewCardLeave = useCallback(() => {
    previewCardHoverRef.current = false
    scheduleHidePreview()
  }, [scheduleHidePreview])

  useEffect(
    () => () => {
      cancelShowDelay()
      cancelHideDelay()
    },
    [cancelHideDelay, cancelShowDelay],
  )

  return {
    hoverId,
    previewId,
    showPreview: !!previewId && !overlayBlocked,
    onHoverStart,
    onHoverEnd,
    onPreviewCardEnter,
    onPreviewCardLeave,
    clearPreview,
    armFocusHover,
  }
}
